import React, { useState, useEffect } from "react";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PageBanner from "../components/PageBanner";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Gallery() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const res = await axios.get(`${API}/gallery`);
        // Process images to handle local uploads
        const processedImages = res.data.map(img => ({
          ...img,
          url: img.url.startsWith('/uploads/') ? `${BACKEND_URL}${img.url}` : img.url
        }));
        setImages(processedImages);
      } catch (error) {
        console.error("Error fetching gallery:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchGallery();
  }, []);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedImage) return;
      
      if (e.key === "ArrowLeft") {
        navigatePrevious();
      } else if (e.key === "ArrowRight") {
        navigateNext();
      } else if (e.key === "Escape") {
        setSelectedImage(null);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedImage, selectedIndex, images]);

  // Navigation functions
  const navigateNext = () => {
    const nextIndex = (selectedIndex + 1) % images.length;
    setSelectedIndex(nextIndex);
    setSelectedImage(images[nextIndex]);
  };

  const navigatePrevious = () => {
    const prevIndex = (selectedIndex - 1 + images.length) % images.length;
    setSelectedIndex(prevIndex);
    setSelectedImage(images[prevIndex]);
  };

  // Handle touch swipe for mobile
  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      navigateNext();
    } else if (isRightSwipe) {
      navigatePrevious();
    }

    setTouchStart(0);
    setTouchEnd(0);
  };

  const openLightbox = (image, index) => {
    setSelectedImage(image);
    setSelectedIndex(index);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-[#FDFBF7]" data-testid="gallery-page">
      <Header />
      
      <PageBanner
        title="GALLERY"
        breadcrumbs={[{ name: "GALLERY" }]}
        backgroundImage="/assets/gallery_banner.png"
      />

      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-[#FEF3E2] to-[#FFE5CC] rounded-full mb-4">
              <div className="w-2 h-2 bg-[#E67E22] rounded-full animate-pulse"></div>
              <span className="text-[#E67E22] text-sm font-bold tracking-widest">VISUAL JOURNEY</span>
            </div>
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-[#2C3E50] mb-4">
              Explore Our <span className="gradient-text">Gallery</span>
            </h2>
            <p className="text-[#7F8C8D] text-lg max-w-2xl mx-auto">Discover the beauty of WyldWoods through our collection</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {images.map((image, index) => (
              <div
                key={image.id || index}
                className="relative group cursor-pointer overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 card-hover animate-scale-in"
                style={{ animationDelay: `${index * 50}ms` }}
                onClick={() => openLightbox(image, index)}
                data-testid={`gallery-image-${index}`}
              >
                <div className="relative overflow-hidden h-80">
                  <img
                    src={image.url}
                    alt={image.caption || `Gallery image ${index + 1}`}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 group-hover:rotate-2"
                  />
                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  
                  {/* Animated border */}
                  <div className="absolute inset-0 border-4 border-transparent group-hover:border-[#E67E22] transition-colors duration-500 rounded-2xl"></div>
                  
                  {/* Caption */}
                  <div className="absolute inset-x-0 bottom-0 p-6 transform translate-y-full group-hover:translate-y-0 transition-transform duration-500">
                    <p className="text-white text-lg font-semibold mb-2">{image.caption}</p>
                    <div className="w-16 h-1 bg-gradient-to-r from-[#E67E22] to-[#F39C12] rounded-full"></div>
                  </div>
                  
                  {/* View icon */}
                  <div className="absolute top-4 right-4 w-12 h-12 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transform scale-0 group-hover:scale-100 transition-all duration-500">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox - Enhanced with Navigation */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in"
          onClick={() => setSelectedImage(null)}
          data-testid="lightbox"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Close Button */}
          <button
            className="absolute top-6 right-6 w-14 h-14 bg-white/10 backdrop-blur-md hover:bg-gradient-to-r hover:from-[#E67E22] hover:to-[#F39C12] text-white rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 hover:rotate-90 z-50"
            onClick={() => setSelectedImage(null)}
            data-testid="lightbox-close"
            aria-label="Close lightbox"
          >
            <X size={28} />
          </button>

          {/* Previous Button */}
          <button
            className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-14 h-14 bg-white/10 backdrop-blur-md hover:bg-gradient-to-r hover:from-[#E67E22] hover:to-[#F39C12] text-white rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 z-50 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={(e) => {
              e.stopPropagation();
              navigatePrevious();
            }}
            aria-label="Previous image"
            data-testid="lightbox-prev"
          >
            <ChevronLeft size={32} />
          </button>

          {/* Next Button */}
          <button
            className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-14 h-14 bg-white/10 backdrop-blur-md hover:bg-gradient-to-r hover:from-[#E67E22] hover:to-[#F39C12] text-white rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 z-50 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={(e) => {
              e.stopPropagation();
              navigateNext();
            }}
            aria-label="Next image"
            data-testid="lightbox-next"
          >
            <ChevronRight size={32} />
          </button>

          {/* Image Counter */}
          <div className="absolute top-6 left-6 bg-white/10 backdrop-blur-md px-4 py-2 rounded-full z-50">
            <span className="text-white font-semibold">
              {selectedIndex + 1} / {images.length}
            </span>
          </div>
          
          <div className="relative max-w-6xl w-full animate-scale-in">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src={selectedImage.url}
                alt={selectedImage.caption}
                className="w-full max-h-[85vh] object-contain"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
            
            {selectedImage.caption && (
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-8 rounded-b-2xl">
                <p className="text-white text-xl md:text-2xl font-semibold text-center">
                  {selectedImage.caption}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
